import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { colorsTheme } from 'resources/theme/styled/colors';
import { SubmitButtonsFormValues } from '../SubmitButtons';
import { fundingValidationSchema } from 'validations/intake-form/intake-form-steps';
import { useEntityIntakeFormContext } from '../../EntityIntakeFormContext';
import {
  AccountUsePercentageOptions,
  AmountOptions,
  CounterPartyOptions,
  SourceOfRevenueOptions,
  TimeframeOptions,
} from 'enums/onboarding/crm';

// components
import InnerForm from './InnerForm';
import DivAlignCenter from 'components/Additional/DivAlignCenter';
import SectionTitleNavigateButton from '../SectionTitleNavigateButton';
import { AutocompleteOption, Form, SectionIntro } from '@ui';

export interface FormValuesModel extends SubmitButtonsFormValues {
  initialDeposit: AmountOptions | null;
  initialDepositAmount: number | null;
  sourceOfInitialDeposit: SourceOfRevenueOptions[];
  sourceOfInitialDepositOtherDetails: string;
  countryOfInitialDeposit: string[];
  countryOfInitialDepositInitialOptions?: AutocompleteOption[];
  timeframeOfInitialDeposit: TimeframeOptions | null;
  timeframeOfInitialDepositInformation: string;
  holdAccountsAnywhereElse: boolean | null;
  otherBankAccountsInfo: string;
  issuesWithOtherBanks: string;
  isPrimaryBankAccount: boolean | null;
  counterpartyType: CounterPartyOptions | null;
  thirdPartyName: string;
  thirdPartyRelationship: string;
  plansToOpenExternalBankAccounts: boolean | null;
  plansToOpenExternalBankAccountsDetails: string;
  accountUsePercentage: AccountUsePercentageOptions | null;
}

interface IProps {
  disabled?: boolean;
  renderNavigateButtonInTitle?: boolean;
}

const Funding = ({ disabled, renderNavigateButtonInTitle }: IProps) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const {
    intakeForm,
    navigateToNextStep,
    navigateToPreviousStep,
    updateIntakeFormFields,
  } = useEntityIntakeFormContext();

  const initialValues = useMemo<FormValuesModel | null>(() => {
    if (!intakeForm) {
      return null;
    }
    const intakeFormFields = intakeForm.fields;

    let countryOfInitialDepositInitialOptions:
      | AutocompleteOption[]
      | undefined = undefined;

    if (
      intakeFormFields?.initialDepositCountries &&
      intakeFormFields.initialDepositCountries.length
    ) {
      countryOfInitialDepositInitialOptions =
        intakeFormFields.initialDepositCountries.map((e) => ({
          id: e,
          label: intakeForm.metadata.countries[e],
        }));
    }

    return {
      submitType: null,
      initialDeposit: intakeFormFields.initialDeposit || null,
      initialDepositAmount: intakeFormFields.initialDepositAmount || null,
      sourceOfInitialDeposit: intakeFormFields.sourceOfFunds || [],
      sourceOfInitialDepositOtherDetails:
        intakeFormFields.sourceOfFundsOtherDetails || '',
      countryOfInitialDeposit: intakeFormFields.initialDepositCountries || [],
      countryOfInitialDepositInitialOptions,
      timeframeOfInitialDeposit:
        intakeFormFields.intendToTransferFundsFrame || null,
      timeframeOfInitialDepositInformation:
        intakeFormFields.intendToTransferFundsFrameDetails || '',
      holdAccountsAnywhereElse:
        typeof intakeFormFields.hasOtherBanks === 'boolean'
          ? !!intakeFormFields.hasOtherBanks
          : null,
      otherBankAccountsInfo: intakeFormFields.otherBanks || '',
      issuesWithOtherBanks: intakeFormFields.otherBankFeedback || '',
      isPrimaryBankAccount:
        typeof intakeFormFields.isPrimaryAccount === 'boolean'
          ? !!intakeFormFields.isPrimaryAccount
          : null,
      counterpartyType: intakeFormFields.counterpartyType || null,
      thirdPartyName: intakeFormFields.thirdPartyName || '',
      thirdPartyRelationship: intakeFormFields.thirdPartyRelationship || '',
      plansToOpenExternalBankAccounts:
        typeof intakeFormFields.plansToOpenExternalBankAccounts === 'boolean'
          ? !!intakeFormFields.plansToOpenExternalBankAccounts
          : null,
      plansToOpenExternalBankAccountsDetails:
        intakeFormFields.plansToOpenExternalBankAccountsDetails || '',
      accountUsePercentage: intakeFormFields.accountUsePercentage || null,
    };
  }, [intakeForm]);

  const handleSubmit = async (values: FormValuesModel) => {
    if (!intakeForm) {
      return;
    }
    await updateIntakeFormFields(
      {
        initialDeposit: values.initialDeposit || null,
        initialDepositAmount:
          values.initialDeposit === AmountOptions.Above1m
            ? values.initialDepositAmount || null
            : null,
        sourceOfFunds: values.sourceOfInitialDeposit.length
          ? values.sourceOfInitialDeposit
          : null,
        sourceOfFundsOtherDetails: values.sourceOfInitialDeposit.includes(
          SourceOfRevenueOptions.Other,
        )
          ? values.sourceOfInitialDepositOtherDetails || null
          : null,
        initialDepositCountries: values.countryOfInitialDeposit.length
          ? values.countryOfInitialDeposit
          : null,
        intendToTransferFundsFrame: values.timeframeOfInitialDeposit || null,
        intendToTransferFundsFrameDetails:
          values.timeframeOfInitialDeposit === TimeframeOptions.NotSure
            ? values.timeframeOfInitialDepositInformation || null
            : null,
        hasOtherBanks:
          typeof values.holdAccountsAnywhereElse === 'boolean'
            ? values.holdAccountsAnywhereElse
            : null,
        otherBanks: values.holdAccountsAnywhereElse
          ? values.otherBankAccountsInfo || null
          : null,
        otherBankFeedback: values.holdAccountsAnywhereElse
          ? values.issuesWithOtherBanks || null
          : null,
        isPrimaryAccount: values.holdAccountsAnywhereElse
          ? typeof values.isPrimaryBankAccount === 'boolean'
            ? values.isPrimaryBankAccount
            : null
          : null,
        counterpartyType: values.counterpartyType || null,
        thirdPartyName:
          values.counterpartyType === CounterPartyOptions.ThirdParty
            ? values.thirdPartyName || null
            : null,
        thirdPartyRelationship:
          values.counterpartyType === CounterPartyOptions.ThirdParty
            ? values.thirdPartyRelationship || null
            : null,
        plansToOpenExternalBankAccounts:
          values.holdAccountsAnywhereElse &&
          values.isPrimaryBankAccount === false
            ? typeof values.plansToOpenExternalBankAccounts === 'boolean'
              ? values.plansToOpenExternalBankAccounts
              : null
            : null,
        plansToOpenExternalBankAccountsDetails:
          values.holdAccountsAnywhereElse &&
          values.isPrimaryBankAccount === false &&
          values.plansToOpenExternalBankAccounts === true
            ? values.plansToOpenExternalBankAccountsDetails || null
            : null,
        accountUsePercentage: values.holdAccountsAnywhereElse
          ? values.accountUsePercentage || null
          : null,
      },
      undefined,
    );
    switch (values.submitType) {
      case 'save-continue':
        navigateToNextStep();
        break;
      case 'back':
        navigateToPreviousStep();
        break;
    }
  };

  return (
    <>
      <SectionIntro
        hideDivider
        titleColor={colorsTheme.colorWhite}
        titleVariant={disabled ? 'h5' : undefined}
        title={
          <DivAlignCenter>
            {t('intake_form.funding.section_title')}
            {renderNavigateButtonInTitle ? (
              <SectionTitleNavigateButton navigateToStepKey="funding" />
            ) : null}
          </DivAlignCenter>
        }
      />
      <Form
        disabled={disabled}
        onSubmit={handleSubmit}
        renderForm={<InnerForm />}
        initialValues={initialValues}
        validationSchema={fundingValidationSchema}
      />
    </>
  );
};

export default Funding;
